import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { CreditCard, Plus, X, Search, DollarSign, Lock, FileText, TrendingUp, ShoppingBag, Store, Clock, ArrowDownCircle, ArrowUpCircle, Package, Table, Truck, Power } from 'lucide-react';
import toast from 'react-hot-toast';
import ModalDelivery from '../../components/ModalDelivery';
import './Caixa.css';

const Caixa = () => {
  const [caixaAberto, setCaixaAberto] = useState(null);
  const [valorAbertura, setValorAbertura] = useState('');
  const [modalAbertura, setModalAbertura] = useState(false);
  const [modalVenda, setModalVenda] = useState(false);
  const [modalFechamento, setModalFechamento] = useState(false);
  const [valorFechamento, setValorFechamento] = useState('');
  const [relatorioFechamento, setRelatorioFechamento] = useState(null);
  const [carrinho, setCarrinho] = useState([]);
  const [cliente, setCliente] = useState({ nome: '', cpf: '', telefone: '' });
  const [formaPagamento, setFormaPagamento] = useState({ tipo: 'dinheiro', valor: 0, troco: 0 });
  const [tipoVenda, setTipoVenda] = useState('balcao'); // Apenas 'balcao'
  const [enderecoEntrega, setEnderecoEntrega] = useState({
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: 'Cachoeiro de Itapemirim',
    estado: 'ES',
    cep: '',
    referencia: ''
  });
  const [dataVencimentoFiado, setDataVencimentoFiado] = useState(() => {
    const data = new Date();
    data.setDate(data.getDate() + 30);
    return data.toISOString().split('T')[0];
  });
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [modalPrevia, setModalPrevia] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('caixa'); // 'caixa' ou 'mesas'
  const [mesaSelecionada, setMesaSelecionada] = useState('');
  const [comandaSelecionadaModal, setComandaSelecionadaModal] = useState(null);
  const [modalDelivery, setModalDelivery] = useState(false);
  const [aceitaPedidos, setAceitaPedidos] = useState(true);
  const queryClient = useQueryClient();

  // Forçar refresh dos produtos
  const refreshProdutos = () => {
    queryClient.invalidateQueries(['produtos-caixa', estabelecimento?._id]);
  };

  // Funções para mesas
  const handleBuscarMesa = () => {
    if (!mesaSelecionada.trim()) {
      toast.error('Informe o número da mesa');
      return;
    }
    refetchComandaMesa();
  };

  const handlePagarComanda = () => {
    if (!comandaMesaData) {
      toast.error('Comanda não encontrada');
      return;
    }
    setComandaSelecionadaModal(comandaMesaData);
    setModalVenda(true);
  };

  const handleSelecionarMesa = (comanda) => {
    setComandaSelecionadaModal(comanda);
    setModalVenda(true);
  };

  const handlePreviewImpressao = async (comandaId) => {
    try {
      const response = await api.get(`/api/comandas/${comandaId}`);
      const comanda = response.data.data.comanda;
      const estabelecimentoResponse = await api.get('/api/estabelecimentos/me/estabelecimento');
      const estabelecimentoData = estabelecimentoResponse.data.data.estabelecimento;
      
      const dataHora = new Date(comanda.dataAbertura).toLocaleString('pt-BR');
      let texto = '';
      
      texto += '='.repeat(50) + '\n';
      texto += '        [LOGO CANTINHO DO SABOR]\n';
      texto += '='.repeat(50) + '\n';
      texto += `${estabelecimentoData?.nome || 'CANTINHO DO SABOR'}\n`;
      texto += 'COMANDA DE CONFERÊNCIA\n';
      texto += '-'.repeat(50) + '\n';
      texto += `COMANDA: ${comanda.numero}\n`;
      texto += `MESA: ${comanda.mesa}\n`;
      texto += `DATA: ${dataHora}\n`;
      texto += '-'.repeat(50) + '\n';
      texto += `CLIENTE: ${comanda.cliente.nome}\n`;
      texto += '-'.repeat(50) + '\n';
      texto += 'ITENS:\n';
      
      comanda.itens.forEach((item) => {
        const produto = item.produto || {};
        texto += `${item.quantidade}x ${produto.nome || 'Produto'}\n`;
        if (item.observacoes) texto += `   📝 ${item.observacoes}\n`;
        texto += `   R$ ${item.subtotal.toFixed(2)}\n\n`;
      });
      
      texto += '-'.repeat(50) + '\n';
      texto += `TOTAL: R$ ${comanda.total.toFixed(2)}\n`;
      texto += '='.repeat(50) + '\n';
      
      // Mostrar preview em modal
      toast.success('Preview gerado! Verifique o console.');
      console.log('\n' + texto);
    } catch (error) {
      toast.error('Erro ao gerar preview: ' + (error.response?.data?.message || error.message));
    }
  };

  const imprimirComandaMutation = useMutation(
    (comandaId) => api.post(`/api/comandas/${comandaId}/imprimir`),
    {
      onSuccess: () => {
        toast.success('Comanda impressa com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao imprimir comanda');
      }
    }
  );

  // Fechar comanda e processar pagamento
  const fecharComandaMutation = useMutation(
    ({ comandaId, formaPagamento, valorRecebido }) => {
      return api.post(`/api/comandas/${comandaId}/fechar`, {
        formaPagamento,
        valorRecebido
      });
    },
    {
      onSuccess: async (response, variables) => {
        const comanda = response.data.data.comanda;
        
        toast.success('Comanda paga e venda registrada!');
        setMesaSelecionada('');
        setComandaSelecionadaModal(null);
        setModalVenda(false);
        
        // Limpar cache e refetch
        queryClient.removeQueries(['comanda-mesa']);
        queryClient.removeQueries(['comandas-abertas-caixa']);
        queryClient.removeQueries(['caixa-atual']);
        
        // Refetch imediato
        await refetchCaixa();
        refetchComandaMesa();
        
        // Forçar refetch múltiplo
        setTimeout(() => {
          refetchComandasAbertas();
          refetchCaixa();
        }, 500);
        setTimeout(() => {
          refetchComandasAbertas();
          refetchCaixa();
        }, 1000);
        setTimeout(() => {
          refetchComandasAbertas();
          refetchCaixa();
        }, 2000);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao fechar comanda');
      }
    }
  );

  // Buscar estabelecimento primeiro
  const { data: estabelecimento, refetch: refetchEstabelecimento } = useQuery(
    'estabelecimento-caixa',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        const est = response.data.data.estabelecimento;
        setAceitaPedidos(est?.configuracoes?.aceitaPedidos !== false);
        return est;
      } catch (error) {
        // Se não encontrar estabelecimento do usuário, buscar o padrão
        if (error.response?.status === 404) {
          try {
            const response = await api.get('/api/estabelecimentos/padrao');
            const est = response.data.data.estabelecimento;
            setAceitaPedidos(est?.configuracoes?.aceitaPedidos !== false);
            return est;
          } catch (padraoError) {
            console.error('Erro ao buscar estabelecimento padrão:', padraoError);
            return null;
          }
        }
        throw error;
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  // Mutation para toggle aceitaPedidos
  const toggleAceitaPedidosMutation = useMutation(
    async (novoValor) => {
      return api.patch(`/api/estabelecimentos/${estabelecimento?._id}/aceita-pedidos`, {
        aceitaPedidos: novoValor
      });
    },
    {
      onSuccess: (_, novoValor) => {
        setAceitaPedidos(novoValor);
        toast.success(novoValor ? '✅ Estabelecimento agora aceita pedidos online' : '🚫 Pedidos online bloqueados');
        queryClient.invalidateQueries('estabelecimento-caixa');
        queryClient.invalidateQueries('estabelecimento-padrao'); // Para atualizar no cardápio do cliente
        refetchEstabelecimento();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar status');
      }
    }
  );

  const { data: produtos, isLoading: loadingProdutos, error: produtosError } = useQuery(
    ['produtos-caixa', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimento._id);
      params.append('disponivel', 'true');
      const response = await api.get(`/api/produtos?${params}`);
      const produtosList = response.data.data.produtos || [];
      console.log('✅ Produtos carregados:', produtosList.length);
      return produtosList;
    },
    {
      enabled: !!estabelecimento?._id,
      retry: 2
    }
  );

  const { data: caixaData, refetch: refetchCaixa } = useQuery(
    'caixa-atual',
    async () => {
      if (!estabelecimento?._id) return null;
      try {
        const response = await api.get(`/api/caixa/atual?estabelecimento=${estabelecimento._id}`);
        // Se não houver caixa aberto, retorna null (não é erro)
        const caixa = response.data.data?.caixa || null;
        const entregadores = response.data.data?.entregadores || [];
        return caixa ? { ...caixa, entregadores } : null;
      } catch (error) {
        // Se for 404 ou outro erro, retorna null (sem caixa aberto)
        if (error.response?.status === 404 || error.response?.status === 400) {
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!estabelecimento?._id,
      refetchInterval: 3000,
      retry: false // Não tentar novamente em caso de erro
    }
  );

  // Entregadores vêm junto com o caixa agora
  const entregadores = caixaData?.entregadores || [];

  const abrirCaixaMutation = useMutation(
    (data) => api.post('/api/caixa/abrir', data),
    {
      onSuccess: () => {
        toast.success('Caixa aberto!');
        setModalAbertura(false);
        setValorAbertura('');
        refetchCaixa();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao abrir caixa');
      }
    }
  );

  const criarVendaMutation = useMutation(
    (data) => api.post('/api/caixa/venda', data),
    {
      onSuccess: () => {
        toast.success('Venda realizada!');
        setCarrinho([]);
        setCliente({ nome: '', cpf: '', telefone: '' });
        setFormaPagamento({ tipo: 'dinheiro', valor: 0, troco: 0 });
        setTipoVenda('balcao');
        setEnderecoEntrega({
          rua: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          estado: 'ES',
          cep: '',
          referencia: ''
        });
        const data = new Date();
        data.setDate(data.getDate() + 30);
        setDataVencimentoFiado(data.toISOString().split('T')[0]);
        setModalVenda(false);
        refetchCaixa();
      },
      onError: (error) => {
        console.error('❌ Erro ao criar venda:', error);
        console.error('Response:', error.response?.data);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao criar venda';
        toast.error(errorMessage);
      }
    }
  );

  const fecharCaixaMutation = useMutation(
    (data) => api.post(`/api/caixa/${caixaData?._id}/fechar`, data),
    {
      onSuccess: (response) => {
        toast.success('Caixa fechado com sucesso!');
        setRelatorioFechamento(response.data.data.relatorio);
        setModalFechamento(true);
        refetchCaixa();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao fechar caixa');
      }
    }
  );

  const adicionarProduto = (produto) => {
    const itemExistente = carrinho.find(item => item.produto._id === produto._id);
    if (itemExistente) {
      setCarrinho(carrinho.map(item =>
        item.produto._id === produto._id
          ? { ...item, quantidade: item.quantidade + 1 }
          : item
      ));
    } else {
      setCarrinho([...carrinho, {
        produto,
        quantidade: 1,
        precoUnitario: produto.precoPromocional || produto.preco,
        desconto: 0
      }]);
    }
  };

  // Calcular taxa de entrega (sempre 0 para balcão)
  const calcularTaxaEntrega = () => {
    return 0; // Apenas balcão, sem taxa
  };

  const calcularTotal = () => {
    const subtotal = carrinho.reduce((acc, item) => {
      return acc + (item.precoUnitario * item.quantidade - item.desconto);
    }, 0);
    const taxaEntrega = calcularTaxaEntrega();
    return subtotal + taxaEntrega;
  };

  // Buscar cliente por telefone
  const buscarClientePorTelefone = async (telefone) => {
    if (!telefone || telefone.length < 10 || !estabelecimento?._id) return;
    
    setBuscandoCliente(true);
    try {
      const telefoneLimpo = telefone.replace(/\D/g, '');
      const response = await api.get(`/api/clientes/buscar/${telefoneLimpo}?estabelecimento=${estabelecimento._id}`);
      
      if (response.data.success && response.data.data.cliente) {
        const clienteEncontrado = response.data.data.cliente;
        setCliente({
          nome: clienteEncontrado.nome || '',
          cpf: clienteEncontrado.cpf || '',
          telefone: clienteEncontrado.telefone || telefone,
          email: clienteEncontrado.email || ''
        });
        toast.success(`✅ Cliente encontrado: ${clienteEncontrado.nome}`);
      } else {
        // Cliente não encontrado - limpar nome se não foi digitado manualmente
        if (!cliente.nome) {
          setCliente(prev => ({ ...prev, nome: '' }));
        }
      }
    } catch (error) {
      // Cliente não encontrado - limpar nome se não foi digitado manualmente
      if (!cliente.nome) {
        setCliente(prev => ({ ...prev, nome: '' }));
      }
    } finally {
      setBuscandoCliente(false);
    }
  };

  const finalizarVenda = () => {
    if (carrinho.length === 0) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }

    if (!caixaData?._id) {
      toast.error('Caixa não encontrado');
      return;
    }

    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado');
      return;
    }

    const isFiado = formaPagamento.tipo === 'fiado';
    
    // ✅ Validação obrigatória para vendas fiadas
    if (isFiado) {
      if (!cliente.nome || cliente.nome.trim() === '') {
        toast.error('Nome do cliente é obrigatório para vendas fiadas');
        return;
      }
      if (!cliente.telefone || cliente.telefone.replace(/\D/g, '').length < 10) {
        toast.error('Telefone do cliente é obrigatório para vendas fiadas');
        return;
      }
      if (!dataVencimentoFiado) {
        toast.error('Data de vencimento é obrigatória para vendas fiadas');
        return;
      }
    }

    const subtotal = carrinho.reduce((acc, item) => {
      return acc + (item.precoUnitario * item.quantidade - (item.desconto || 0));
    }, 0);
    const taxaEntrega = calcularTaxaEntrega();
    const total = subtotal + taxaEntrega;

    console.log('📤 Dados da venda sendo enviados:');
    console.log('   Tipo:', tipoVenda);
    console.log('   Subtotal:', subtotal);
    console.log('   Taxa de entrega:', taxaEntrega);
    console.log('   Total:', total);

    const dadosVenda = {
      caixa: caixaData._id,
      estabelecimento: estabelecimento._id,
      tipoVenda: tipoVenda,
      itens: carrinho.map(item => ({
        produto: item.produto._id,
        quantidade: item.quantidade,
        precoUnitario: item.precoUnitario,
        desconto: item.desconto || 0
      })),
      cliente,
      enderecoEntrega: undefined,
      subtotal: subtotal,
      taxaEntrega: taxaEntrega, // ✅ ENVIAR TAXA EXPLICITAMENTE
      total: total, // ✅ TOTAL JÁ INCLUI A TAXA
      formaPagamento: {
        ...formaPagamento,
        tipo: formaPagamento.tipo,
        valor: isFiado ? total : (formaPagamento.tipo === 'dinheiro' ? parseFloat(formaPagamento.valor) : total),
        troco: isFiado ? 0 : (formaPagamento.tipo === 'dinheiro' ? Math.max(0, parseFloat(formaPagamento.valor) - total) : 0)
      }
    };

    // Adicionar dataVencimentoFiado apenas se for fiado
    if (isFiado && dataVencimentoFiado) {
      dadosVenda.dataVencimentoFiado = dataVencimentoFiado;
    }

    console.log('📤 Enviando dados da venda:', dadosVenda);

    criarVendaMutation.mutate(dadosVenda);
  };

  useEffect(() => {
    if (caixaData) {
      setCaixaAberto(caixaData);
    } else {
      setCaixaAberto(null);
    }
  }, [caixaData]);


  // Buscar todas as comandas abertas para exibir como cards
  const { data: comandasAbertas, refetch: refetchComandasAbertas, isLoading: loadingComandas } = useQuery(
    ['comandas-abertas-caixa', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      try {
        const response = await api.get(`/api/comandas?estabelecimento=${estabelecimento._id}&status=aberta`);
        return response.data.data.comandas || [];
      } catch (error) {
        return [];
      }
    },
    {
      enabled: !!estabelecimento?._id && abaAtiva === 'mesas',
      refetchInterval: 2000,
      refetchOnWindowFocus: true,
      staleTime: 0,
      cacheTime: 0
    }
  );

  // Buscar comanda por mesa (para busca manual)
  const { data: comandaMesaData, refetch: refetchComandaMesa } = useQuery(
    ['comanda-mesa', estabelecimento?._id, mesaSelecionada],
    async () => {
      if (!estabelecimento?._id || !mesaSelecionada) return null;
      try {
        const response = await api.get(`/api/comandas/mesa/${mesaSelecionada}?estabelecimento=${estabelecimento._id}`);
        return response.data.data.comanda;
      } catch (error) {
        if (error.response?.status === 404) return null;
        throw error;
      }
    },
    {
      enabled: !!estabelecimento?._id && !!mesaSelecionada && abaAtiva === 'mesas',
      refetchInterval: 3000
    }
  );

  // Atualizar formaPagamento quando mudar comanda selecionada
  useEffect(() => {
    if (abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)) {
      const comanda = comandaSelecionadaModal || comandaMesaData;
      if (comanda) {
        const total = parseFloat(comanda.total) || 0;
        setFormaPagamento(prev => ({
          ...prev,
          valor: prev.tipo === 'dinheiro' ? (prev.valor > 0 ? prev.valor : total) : total,
          troco: prev.tipo === 'dinheiro' ? (prev.troco || 0) : 0
        }));
      }
    }
  }, [comandaSelecionadaModal, comandaMesaData, abaAtiva]);

  if (!caixaAberto) {
    return (
      <div className="caixa-admin">
        <div className="caixa-fechado">
          <CreditCard size={64} />
          <h2>Caixa Fechado</h2>
          <p>Abra o caixa para começar as vendas</p>
          <button className="btn btn-primary" onClick={() => setModalAbertura(true)}>
            Abrir Caixa
          </button>
        </div>

        {modalAbertura && (
          <div className="modal-overlay" onClick={() => setModalAbertura(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Abrir Caixa</h3>
              <div className="form-group">
                <label>Troco (valor inicial do caixa)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={valorAbertura || ''}
                  onChange={(e) => {
                    const valor = e.target.value;
                    if (valor === '' || valor === null || valor === undefined) {
                      setValorAbertura('');
                    } else {
                      const num = parseFloat(valor);
                      setValorAbertura(isNaN(num) ? '' : valor);
                    }
                  }}
                  placeholder="0.00"
                />
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setModalAbertura(false)}>
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (!estabelecimento?._id) {
                      toast.error('Estabelecimento não encontrado. Aguarde o carregamento...');
                      return;
                    }
                    
                    const valor = parseFloat(valorAbertura) || 0;
                    if (isNaN(valor) || valor < 0) {
                      toast.error('Valor de abertura inválido');
                      return;
                    }
                    
                    abrirCaixaMutation.mutate({
                      estabelecimento: estabelecimento._id,
                      valorAbertura: valor
                    });
                  }}
                  disabled={abrirCaixaMutation.isLoading || !estabelecimento?._id}
                >
                  {abrirCaixaMutation.isLoading ? 'Abrindo...' : 'Abrir'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Calcular estatísticas - usar dados do backend que já vêm com estatísticas
  const totalVendasCount = caixaData?.totalVendas || caixaData?.vendas?.length || 0;
  const vendasFinalizadasCount = caixaData?.vendasFinalizadas || caixaData?.vendas?.filter(v => v?.status === 'finalizada').length || 0;
  
  // Garantir que vendas estejam populadas
  const vendasArray = caixaData?.vendas || [];
  const vendasFinalizadas = vendasArray.filter(v => {
    if (!v) return false;
    if (typeof v === 'object') {
      return v.status === 'finalizada';
    }
    return false;
  });
  
  // ✅ Separar vendas fiadas das vendas normais
  const vendasFiadas = vendasFinalizadas.filter(v => v.isFiado === true);
  const vendasNormais = vendasFinalizadas.filter(v => !v.isFiado || v.isFiado === false);
  
  // Calcular totais (APENAS vendas normais, não incluir fiadas)
  const totalVendas = vendasNormais.reduce((acc, v) => acc + (parseFloat(v.total) || 0), 0);
  const totalVendasFiadas = vendasFiadas.reduce((acc, v) => acc + (parseFloat(v.total) || 0), 0);
  const valorEmCaixa = (parseFloat(caixaData?.valorAbertura) || 0) + totalVendas;
  
  // Usar dados do backend se disponível
  const vendasFiadasData = caixaData?.vendasFiadas || { quantidade: vendasFiadas.length, total: totalVendasFiadas };
  
  // Contar vendas por tipo
  const vendasBalcao = vendasFinalizadas.filter(v => v.tipoVenda === 'balcao' || !v.tipoVenda).length;
  const vendasChat = vendasFinalizadas.filter(v => v.tipoVenda === 'chat').length;
  const vendasRetirada = vendasFinalizadas.filter(v => v.tipoVenda === 'retirada').length;

  const statsCards = [
    {
      title: 'Total em Caixa',
      value: `R$ ${valorEmCaixa.toFixed(2)}`,
      icon: DollarSign,
      premiumClass: 'premium-green',
      subtitle: `Abertura: R$ ${(caixaData?.valorAbertura || 0).toFixed(2)}`
    },
    {
      title: 'Total Vendido',
      value: `R$ ${totalVendas.toFixed(2)}`,
      icon: TrendingUp,
      premiumClass: 'premium-purple',
      subtitle: 'Vendas finalizadas'
    },
    {
      title: 'Total de Vendas',
      value: `${totalVendasCount}`,
      icon: ShoppingBag,
      premiumClass: 'premium-blue',
      subtitle: `${vendasFinalizadasCount} finalizadas | Balcão: ${vendasBalcao} | Chat: ${vendasChat}`
    },
    {
      title: 'Tipo de Venda',
      value: 'BALCÃO',
      icon: Store,
      premiumClass: 'premium-orange',
      subtitle: 'PDV - Ponto de Venda'
    }
  ];

  return (
    <div className="caixa-admin">
      {/* Controle de status do estabelecimento */}
      {estabelecimento && (
        <div style={{
          background: aceitaPedidos ? '#d4edda' : '#f8d7da',
          border: `2px solid ${aceitaPedidos ? '#28a745' : '#dc3545'}`,
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: aceitaPedidos ? '#28a745' : '#dc3545',
              animation: aceitaPedidos ? 'pulse 2s infinite' : 'none'
            }} />
            <div>
              <strong style={{ fontSize: '1.1rem', display: 'block', marginBottom: '0.25rem' }}>
                {aceitaPedidos ? '✅ Estabelecimento ABERTO para pedidos online' : '🚫 Estabelecimento FECHADO para pedidos online'}
              </strong>
              <small style={{ color: '#666' }}>
                {aceitaPedidos 
                  ? 'Clientes podem fazer pedidos pelo site e WhatsApp' 
                  : 'Novos pedidos online estão bloqueados (site e WhatsApp)'}
              </small>
            </div>
          </div>
          <button
            onClick={() => toggleAceitaPedidosMutation.mutate(!aceitaPedidos)}
            disabled={toggleAceitaPedidosMutation.isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              background: aceitaPedidos ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: toggleAceitaPedidosMutation.isLoading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              opacity: toggleAceitaPedidosMutation.isLoading ? 0.7 : 1
            }}
          >
            <Power size={18} />
            {aceitaPedidos ? 'FECHAR' : 'ABRIR'} PEDIDOS
          </button>
        </div>
      )}

      {/* Abas */}
      <div className="caixa-abas">
        <button
          className={`caixa-aba ${abaAtiva === 'caixa' ? 'active' : ''}`}
          onClick={() => setAbaAtiva('caixa')}
        >
          <ShoppingBag size={18} />
          Caixa
        </button>
        <button
          className={`caixa-aba ${abaAtiva === 'mesas' ? 'active' : ''}`}
          onClick={() => {
            setAbaAtiva('mesas');
            // Forçar refetch ao mudar para aba mesas
            setTimeout(() => {
              refetchComandasAbertas();
            }, 100);
          }}
        >
          <Table size={18} />
          Mesas
        </button>
      </div>

      {/* Header Compacto */}
      <div className="caixa-header-compact">
        <div className="caixa-header-left">
          <h2>💳 PDV - Ponto de Venda</h2>
          <span className="caixa-status">
            <Clock size={14} />
            Caixa #{caixaData?.numero || 'Aberto'} - {new Date().toLocaleTimeString('pt-BR')}
          </span>
        </div>
        
        <div className="stats-grid-compact">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className={`stat-card-compact ${stat.premiumClass}`}>
                <Icon size={18} />
                <div className="stat-content-compact">
                  <p className="stat-title-compact">{stat.title}</p>
                  <p className="stat-value-compact">{stat.value}</p>
                </div>
              </div>
            );
          })}
          {/* ✅ Card de Vendas Fiadas */}
          {vendasFiadasData.quantidade > 0 && (
            <div className="stat-card-compact premium-orange" style={{ border: '2px solid #f59e0b' }}>
              <Clock size={18} />
              <div className="stat-content-compact">
                <p className="stat-title-compact">Vendas Fiadas</p>
                <p className="stat-value-compact">{vendasFiadasData.quantidade}</p>
                <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                  R$ {vendasFiadasData.total?.toFixed(2) || totalVendasFiadas.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button 
            className="btn-delivery-caixa"
            onClick={() => setModalDelivery(true)}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              fontWeight: '700',
              fontSize: '0.9375rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
            }}
          >
            <Truck size={20} />
            DELIVERY
          </button>
          <button 
            className="btn-fechar-caixa-compact"
            onClick={() => {
              const valorEsperado = valorEmCaixa;
              setValorFechamento(valorEsperado.toFixed(2));
              setModalFechamento(true);
            }}
          >
            <Lock size={16} />
            Fechar
          </button>
        </div>
      </div>

      {abaAtiva === 'mesas' ? (
        <div className="mesas-section">
          <div className="mesas-header">
            <h3>🍽️ Pagamento de Mesas</h3>
            <div className="mesas-header-actions">
              <div className="buscar-mesa">
                <input
                  type="text"
                  value={mesaSelecionada}
                  onChange={(e) => setMesaSelecionada(e.target.value)}
                  placeholder="Buscar mesa específica..."
                  onKeyPress={(e) => e.key === 'Enter' && handleBuscarMesa()}
                />
                <button className="btn-buscar-mesa" onClick={handleBuscarMesa}>
                  <Search size={18} />
                  Buscar
                </button>
              </div>
              <button 
                className="btn-refresh-mesas"
                onClick={() => {
                  refetchComandasAbertas();
                  toast.success('Atualizando comandas...');
                }}
                title="Atualizar lista de comandas"
              >
                🔄 Atualizar
              </button>
            </div>
          </div>

          {/* Cards de Mesas */}
          {loadingComandas ? (
            <div className="empty-mesa">
              <Package size={48} />
              <p>Carregando comandas...</p>
            </div>
          ) : comandasAbertas && comandasAbertas.length > 0 ? (
            <div className="mesas-grid">
              {comandasAbertas.map((comanda) => (
                <div key={comanda._id} className="mesa-card-caixa" onClick={() => handleSelecionarMesa(comanda)}>
                  <div className="mesa-card-header">
                    <h4>Mesa {comanda.mesa}</h4>
                    <span className="mesa-status-badge aberta">Aberta</span>
                  </div>
                  <div className="mesa-card-info">
                    <p><strong>Cliente:</strong> {comanda.cliente?.nome || 'Não informado'}</p>
                    <p><strong>Comanda:</strong> #{comanda.numero}</p>
                    <p><strong>Itens:</strong> {comanda.itens?.length || 0}</p>
                  </div>
                  <div className="mesa-card-total">
                    <span>Total:</span>
                    <strong>R$ {(comanda.total || 0).toFixed(2)}</strong>
                  </div>
                  <div className="mesa-card-acoes">
                    <button 
                      className="btn-preview-mesa"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewImpressao(comanda._id);
                      }}
                    >
                      <FileText size={16} />
                      Preview
                    </button>
                    <button 
                      className="btn-pagar-mesa"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelecionarMesa(comanda);
                      }}
                    >
                      <CreditCard size={16} />
                      Pagar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-mesa">
              <Table size={48} />
              <p>Nenhuma comanda aberta no momento</p>
              <button 
                className="btn-refresh-mesas"
                onClick={() => {
                  refetchComandasAbertas();
                  toast.success('Atualizando...');
                }}
                style={{ marginTop: '1rem' }}
              >
                🔄 Atualizar
              </button>
            </div>
          )}

          {/* Busca manual */}
          {comandaMesaData ? (
            <div className="comanda-mesa-detalhes">
              <div className="comanda-mesa-header">
                <div>
                  <h4>Mesa {comandaMesaData.mesa}</h4>
                  <p>Cliente: {comandaMesaData.cliente?.nome || 'Não informado'}</p>
                  <span className="comanda-numero">#{comandaMesaData.numero}</span>
                </div>
                <button 
                  className="btn-imprimir-comanda" 
                  onClick={() => handlePreviewImpressao(comandaMesaData._id)}
                >
                  <FileText size={18} />
                  Preview
                </button>
              </div>
              <div className="comanda-mesa-itens">
                <h5>Itens da Comanda:</h5>
                {comandaMesaData.itens?.map((item, idx) => (
                  <div key={idx} className="item-mesa">
                    <div className="item-mesa-info">
                      <strong>{item.quantidade}x</strong>
                      <div>
                        <span>{item.produto?.nome || 'Produto'}</span>
                        {item.observacoes && <small>📝 {item.observacoes}</small>}
                      </div>
                    </div>
                    <span className="item-mesa-preco">R$ {(item.subtotal || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="comanda-mesa-total">
                <span>Total a Pagar:</span>
                <strong>R$ {(comandaMesaData.total || 0).toFixed(2)}</strong>
              </div>
              <button className="btn-pagar-comanda" onClick={handlePagarComanda}>
                <CreditCard size={20} />
                Processar Pagamento
              </button>
            </div>
          ) : mesaSelecionada ? (
            <div className="empty-mesa">
              <Table size={48} />
              <p>Nenhuma comanda aberta encontrada para {mesaSelecionada}</p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="caixa-layout-premium">
        {/* Produtos */}
        <div className="produtos-section-premium">
          <div className="section-header">
            <h3>📦 Produtos ({produtos?.length || 0})</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div className="search-produtos-premium">
                <Search size={20} />
                <input type="text" placeholder="Buscar produto..." />
              </div>
              <button 
                onClick={refreshProdutos}
                style={{ 
                  padding: '0.5rem', 
                  background: '#667eea', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
                title="Atualizar produtos"
              >
                🔄
              </button>
            </div>
          </div>
          <div className="produtos-grid-premium">
            {loadingProdutos ? (
              <div className="loading-produtos" style={{ gridColumn: '1 / -1' }}>
                <p>Carregando produtos...</p>
              </div>
            ) : produtosError ? (
              <div className="empty-produtos" style={{ gridColumn: '1 / -1' }}>
                <Package size={48} />
                <p>Erro ao carregar produtos</p>
                <span>{produtosError.message || 'Tente recarregar a página'}</span>
              </div>
            ) : !estabelecimento?._id ? (
              <div className="empty-produtos" style={{ gridColumn: '1 / -1' }}>
                <Package size={48} />
                <p>Carregando estabelecimento...</p>
              </div>
            ) : produtos && Array.isArray(produtos) && produtos.length > 0 ? (
              <>
                {console.log('🎯 Renderizando', produtos.length, 'produtos')}
                {produtos.map((produto, index) => {
                  console.log(`🎨 Produto ${index + 1}:`, produto.nome);
                  return (
                    <div
                      key={produto._id || index}
                      className="produto-card-premium"
                      onClick={() => adicionarProduto(produto)}
                    >
                      {produto.imagens?.[0]?.url && (
                        <div className="produto-image">
                          <img src={produto.imagens[0].url} alt={produto.nome} />
                        </div>
                      )}
                      <div className="produto-info-premium">
                        <h4>{produto.nome}</h4>
                        <p className="produto-preco-premium">
                          R$ {(produto.precoPromocional || produto.preco).toFixed(2)}
                        </p>
                        {produto.estoque && (
                          <span className="produto-estoque-badge">
                            Estoque: {produto.estoque.quantidade}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="empty-produtos" style={{ gridColumn: '1 / -1' }}>
                <Package size={48} />
                <p>Nenhum produto disponível</p>
                <span>Adicione produtos no menu Cardápio</span>
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                  Debug: produtos={produtos ? 'existe' : 'null'}, length={produtos?.length || 0}, isArray={Array.isArray(produtos) ? 'sim' : 'não'}
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Carrinho Premium */}
        <div className="carrinho-section-premium">
          <div className="section-header">
            <h3>🛒 Carrinho de Venda</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{
                padding: '0.5rem',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '0.875rem',
                fontWeight: '600',
                background: '#e0e7ff',
                color: '#3730a3',
                textAlign: 'center'
              }}>
                🛒 Balcão
              </div>
            </div>
          </div>
          
          
          <div className="carrinho-items-premium">
            {carrinho.length === 0 ? (
              <div className="carrinho-vazio">
                <ShoppingBag size={48} />
                <p>Carrinho vazio</p>
                <span>Adicione produtos para iniciar uma venda</span>
              </div>
            ) : (
              carrinho.map((item, idx) => (
                <div key={idx} className="carrinho-item-premium">
                  <div className="item-info-premium">
                    <strong>{item.quantidade}x</strong>
                    <span>{item.produto.nome}</span>
                  </div>
                  <div className="item-actions-premium">
                    <span className="item-preco-premium">
                      R$ {(item.precoUnitario * item.quantidade).toFixed(2)}
                    </span>
                    <button 
                      className="btn-remove-item"
                      onClick={() => setCarrinho(carrinho.filter((_, i) => i !== idx))}
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {carrinho.length > 0 && (
            <div className="carrinho-footer-premium">
              <div style={{ marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  <span>Subtotal dos Produtos:</span>
                  <strong>R$ {carrinho.reduce((acc, item) => acc + (item.precoUnitario * item.quantidade - item.desconto), 0).toFixed(2)}</strong>
                </div>
              </div>
              <div className="total-premium">
                <span>Total da Venda</span>
                <strong>R$ {calcularTotal().toFixed(2)}</strong>
              </div>
              <button
                className="btn-finalizar-premium"
                onClick={() => {
                  setModalVenda(true);
                }}
              >
                <CreditCard size={20} />
                Finalizar Venda de Balcão
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {modalVenda && (
        <div className="modal-overlay" onClick={() => setModalVenda(false)}>
          <div className="modal-content modal-venda" onClick={(e) => e.stopPropagation()}>
            <h3>{abaAtiva === 'mesas' ? 'Pagar Comanda' : 'Finalizar Venda de Balcão'}</h3>
            <p className="badge-modal-balcao">
              {abaAtiva === 'mesas' ? `Comanda ${(comandaSelecionadaModal || comandaMesaData)?.mesa}` : 'Venda direta no PDV'}
            </p>
            {abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData) && (
              <div className="comanda-resumo-modal">
                <p><strong>Cliente:</strong> {(comandaSelecionadaModal || comandaMesaData).cliente?.nome || 'Não informado'}</p>
                <p><strong>Total:</strong> R$ {((comandaSelecionadaModal || comandaMesaData).total || 0).toFixed(2)}</p>
              </div>
            )}
            <div className="form-group">
              <label>
                Nome do Cliente {formaPagamento.tipo === 'fiado' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="text"
                value={cliente.nome}
                onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                placeholder="Nome do cliente"
                required={formaPagamento.tipo === 'fiado'}
                style={formaPagamento.tipo === 'fiado' && !cliente.nome ? { borderColor: '#ef4444' } : {}}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>CPF</label>
                <input
                  type="text"
                  value={cliente.cpf}
                  onChange={(e) => setCliente({ ...cliente, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>
              <div className="form-group">
                <label>
                  Telefone {formaPagamento.tipo === 'fiado' && <span style={{ color: '#ef4444' }}>*</span>}
                  {formaPagamento.tipo !== 'fiado' && <span style={{ fontSize: '0.75rem', opacity: 0.7 }}> (opcional - para cadastro automático)</span>}
                </label>
                <input
                  type="text"
                  value={cliente.telefone}
                  onChange={(e) => {
                    const telefone = e.target.value;
                    setCliente({ ...cliente, telefone });
                    if (telefone.length >= 10) {
                      buscarClientePorTelefone(telefone);
                    }
                  }}
                  placeholder="(00) 00000-0000"
                  disabled={buscandoCliente}
                  required={formaPagamento.tipo === 'fiado'}
                  style={formaPagamento.tipo === 'fiado' && (!cliente.telefone || cliente.telefone.replace(/\D/g, '').length < 10) ? { borderColor: '#ef4444' } : {}}
                />
                {buscandoCliente && <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>Buscando cliente...</small>}
                {formaPagamento.tipo === 'fiado' && cliente.telefone && cliente.telefone.replace(/\D/g, '').length < 10 && (
                  <small style={{ color: '#ef4444', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>Telefone inválido</small>
                )}
              </div>
            </div>
            
            
            <div className="form-group">
              <label>Forma de Pagamento</label>
              <select
                value={formaPagamento.tipo}
                onChange={(e) => {
                  const novoTipo = e.target.value;
                  const total = abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)
                    ? ((comandaSelecionadaModal || comandaMesaData).total || 0)
                    : calcularTotal();
                  
                  setFormaPagamento({
                    ...formaPagamento,
                    tipo: novoTipo,
                    valor: novoTipo === 'dinheiro' ? (formaPagamento.valor || total) : total,
                    troco: 0
                  });
                }}
              >
                <option value="dinheiro">Dinheiro</option>
                <option value="pix">PIX</option>
                <option value="cartao_credito">Cartão de Crédito</option>
                <option value="cartao_debito">Cartão de Débito</option>
                <option value="vale_refeicao">Vale Refeição</option>
                <option value="fiado">Fiado</option>
              </select>
            </div>
            {formaPagamento.tipo === 'dinheiro' && (
              <div className="form-group">
                <label>Valor Recebido</label>
                <input
                  type="number"
                  step="0.01"
                  value={formaPagamento.valor}
                  onChange={(e) => {
                    const valor = parseFloat(e.target.value) || 0;
                    const total = abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)
                      ? ((comandaSelecionadaModal || comandaMesaData).total || 0)
                      : calcularTotal();
                    setFormaPagamento({
                      ...formaPagamento,
                      valor,
                      troco: Math.max(0, valor - total)
                    });
                  }}
                />
                {formaPagamento.troco > 0 && (
                  <p className="troco">Troco: R$ {formaPagamento.troco.toFixed(2)}</p>
                )}
              </div>
            )}
            {formaPagamento.tipo === 'fiado' && (
              <div className="form-group">
                <label>Data de Vencimento <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="date"
                  value={dataVencimentoFiado}
                  onChange={(e) => setDataVencimentoFiado(e.target.value)}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd' }}
                />
                <small style={{ fontSize: '0.75rem', opacity: 0.7, display: 'block', marginTop: '0.25rem' }}>
                  Data em que o cliente deve pagar a conta
                </small>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => {
                setModalVenda(false);
                setComandaSelecionadaModal(null);
              }}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={() => {
                if (abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)) {
                  // Processar pagamento da comanda
                  const comanda = comandaSelecionadaModal || comandaMesaData;
                  const totalComanda = parseFloat(comanda.total) || 0;
                  
                  let valorRecebido = totalComanda;
                  let troco = 0;
                  
                  if (formaPagamento.tipo === 'dinheiro') {
                    valorRecebido = parseFloat(formaPagamento.valor) || totalComanda;
                    troco = Math.max(0, valorRecebido - totalComanda);
                  }
                  
                  const formaPagamentoCompleta = {
                    tipo: formaPagamento.tipo || 'dinheiro',
                    valor: valorRecebido,
                    troco: troco
                  };
                  
                  fecharComandaMutation.mutate({
                    comandaId: comanda._id,
                    formaPagamento: formaPagamentoCompleta,
                    valorRecebido: valorRecebido
                  });
                  setModalVenda(false);
                } else {
                  finalizarVenda();
                }
              }}>
                {abaAtiva === 'mesas' ? 'Confirmar Pagamento' : 'Confirmar Venda'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalFechamento && (
        <div className="modal-overlay" onClick={() => {
          if (relatorioFechamento) {
            setModalFechamento(false);
            setRelatorioFechamento(null);
          }
        }}>
          <div className="modal-content modal-fechamento" onClick={(e) => e.stopPropagation()}>
            {!relatorioFechamento ? (
              <>
                <h3>Fechar Caixa</h3>
                <div className="form-group">
                  <label>Valor de Fechamento</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={valorFechamento}
                    onChange={(e) => setValorFechamento(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setModalFechamento(false)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      const valor = parseFloat(valorFechamento) || 0;
                      if (isNaN(valor) || valor < 0) {
                        toast.error('Valor de fechamento inválido');
                        return;
                      }
                      fecharCaixaMutation.mutate({
                        valorFechamento: valor,
                        observacoes: ''
                      });
                    }}
                    disabled={fecharCaixaMutation.isLoading}
                  >
                    {fecharCaixaMutation.isLoading ? 'Fechando...' : 'Confirmar Fechamento'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="relatorio-header">
                  <FileText size={32} />
                  <h3>Relatório de Fechamento</h3>
                  <p>Caixa #{caixaData?.numero}</p>
                </div>

                <div className="relatorio-content">
                  <div className="relatorio-section">
                    <h4>Resumo Geral</h4>
                    <div className="relatorio-item">
                      <span>Troco (inicial):</span>
                      <strong>R$ {relatorioFechamento.valorAbertura.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Total de Vendas:</span>
                      <strong>R$ {relatorioFechamento.totalVendas.toFixed(2)}</strong>
                    </div>
                    {relatorioFechamento.totaisPorFormaPagamento?.dinheiro?.troco > 0 && (
                      <div className="relatorio-item">
                        <span>Troco Total (Dinheiro):</span>
                        <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.dinheiro.troco.toFixed(2)}</strong>
                      </div>
                    )}
                    <div className="relatorio-item">
                      <span>Quantidade de Vendas:</span>
                      <strong>{relatorioFechamento.quantidadeVendas}</strong>
                    </div>
                    <div className="relatorio-item highlight">
                      <span>Valor Esperado (troco + vendas):</span>
                      <strong>R$ {relatorioFechamento.valorEsperado.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Valor de Fechamento:</span>
                      <strong>R$ {relatorioFechamento.valorFechamento.toFixed(2)}</strong>
                    </div>
                    <div className={`relatorio-item ${relatorioFechamento.diferenca >= 0 ? 'positive' : 'negative'}`}>
                      <span>Diferença:</span>
                      <strong>R$ {relatorioFechamento.diferenca.toFixed(2)}</strong>
                    </div>
                  </div>

                  <div className="relatorio-section">
                    <h4>Detalhamento por Forma de Pagamento</h4>
                    
                    {relatorioFechamento.totaisPorFormaPagamento.dinheiro.quantidade > 0 && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>💵 Dinheiro</span>
                          <strong>{relatorioFechamento.totaisPorFormaPagamento.dinheiro.quantidade} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.dinheiro.total.toFixed(2)}</strong>
                          </div>
                          {relatorioFechamento.totaisPorFormaPagamento.dinheiro.troco > 0 && (
                            <div className="detalhe-item">
                              <span>Troco Total:</span>
                              <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.dinheiro.troco.toFixed(2)}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento.pix.quantidade > 0 && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>📱 PIX</span>
                          <strong>{relatorioFechamento.totaisPorFormaPagamento.pix.quantidade} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.pix.total.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento.cartao_credito.quantidade > 0 && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>💳 Cartão de Crédito</span>
                          <strong>{relatorioFechamento.totaisPorFormaPagamento.cartao_credito.quantidade} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.cartao_credito.total.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento.cartao_debito.quantidade > 0 && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>💳 Cartão de Débito</span>
                          <strong>{relatorioFechamento.totaisPorFormaPagamento.cartao_debito.quantidade} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.cartao_debito.total.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento.vale_refeicao.quantidade > 0 && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>🍽️ Vale Refeição</span>
                          <strong>{relatorioFechamento.totaisPorFormaPagamento.vale_refeicao.quantidade} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.vale_refeicao.total.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento.outro.quantidade > 0 && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>🔀 Outro</span>
                          <strong>{relatorioFechamento.totaisPorFormaPagamento.outro.quantidade} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.outro.total.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ✅ NOVO: Seção de Vendas Fiadas */}
                  {relatorioFechamento.vendasFiadas && relatorioFechamento.vendasFiadas.quantidade > 0 && (
                    <div className="relatorio-section" style={{ border: '2px solid #f59e0b', borderRadius: '8px', padding: '1rem', background: 'rgba(245, 158, 11, 0.1)' }}>
                      <h4 style={{ color: '#f59e0b', marginBottom: '1rem' }}>⏳ Vendas Fiadas (Não somam ao total do dia)</h4>
                      <div className="relatorio-item highlight" style={{ marginBottom: '1rem' }}>
                        <span>Quantidade de Vendas Fiadas:</span>
                        <strong style={{ color: '#f59e0b' }}>{relatorioFechamento.vendasFiadas.quantidade}</strong>
                      </div>
                      <div className="relatorio-item highlight" style={{ marginBottom: '1rem' }}>
                        <span>Total em Fiado:</span>
                        <strong style={{ color: '#f59e0b' }}>R$ {relatorioFechamento.vendasFiadas.total.toFixed(2)}</strong>
                      </div>
                      {relatorioFechamento.vendasFiadas.detalhes && relatorioFechamento.vendasFiadas.detalhes.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                          <h5 style={{ marginBottom: '0.75rem', fontSize: '0.9rem', color: '#f59e0b' }}>Detalhamento:</h5>
                          {relatorioFechamento.vendasFiadas.detalhes.map((venda, idx) => (
                            <div key={idx} style={{ 
                              padding: '0.75rem', 
                              background: 'rgba(255,255,255,0.05)', 
                              borderRadius: '4px', 
                              marginBottom: '0.5rem',
                              borderLeft: '3px solid #f59e0b'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                <span style={{ fontWeight: 'bold' }}>#{venda.numero}</span>
                                <strong style={{ color: '#f59e0b' }}>R$ {venda.total.toFixed(2)}</strong>
                              </div>
                              <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                                Cliente: {venda.cliente} | {new Date(venda.data).toLocaleDateString('pt-BR')}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ✅ NOVO: Detalhamento por Origem */}
                  {relatorioFechamento.vendasPorOrigem && (
                    <div className="relatorio-section">
                      <h4>Detalhamento por Origem</h4>
                      {relatorioFechamento.vendasPorOrigem.balcao && relatorioFechamento.vendasPorOrigem.balcao.quantidade > 0 && (
                        <div className="forma-pagamento-item">
                          <div className="forma-pagamento-header">
                            <span>🏪 Balcão</span>
                            <strong>{relatorioFechamento.vendasPorOrigem.balcao.quantidade} venda(s)</strong>
                          </div>
                          <div className="forma-pagamento-detalhes">
                            <div className="detalhe-item">
                              <span>Total:</span>
                              <strong>R$ {relatorioFechamento.vendasPorOrigem.balcao.total.toFixed(2)}</strong>
                            </div>
                          </div>
                        </div>
                      )}
                      {relatorioFechamento.vendasPorOrigem.chat && relatorioFechamento.vendasPorOrigem.chat.quantidade > 0 && (
                        <div className="forma-pagamento-item">
                          <div className="forma-pagamento-header">
                            <span>💬 WhatsApp</span>
                            <strong>{relatorioFechamento.vendasPorOrigem.chat.quantidade} venda(s)</strong>
                          </div>
                          <div className="forma-pagamento-detalhes">
                            <div className="detalhe-item">
                              <span>Total:</span>
                              <strong>R$ {relatorioFechamento.vendasPorOrigem.chat.total.toFixed(2)}</strong>
                            </div>
                          </div>
                        </div>
                      )}
                      {relatorioFechamento.vendasPorOrigem.retirada && relatorioFechamento.vendasPorOrigem.retirada.quantidade > 0 && (
                        <div className="forma-pagamento-item">
                          <div className="forma-pagamento-header">
                            <span>📦 Retirada</span>
                            <strong>{relatorioFechamento.vendasPorOrigem.retirada.quantidade} venda(s)</strong>
                          </div>
                          <div className="forma-pagamento-detalhes">
                            <div className="detalhe-item">
                              <span>Total:</span>
                              <strong>R$ {relatorioFechamento.vendasPorOrigem.retirada.total.toFixed(2)}</strong>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {relatorioFechamento.movimentacoes && (
                    <div className="relatorio-section">
                      <h4>Movimentações do Período</h4>
                      <div className="relatorio-item">
                        <span>Total de Entradas:</span>
                        <strong>R$ {relatorioFechamento.movimentacoes.totalEntradas.toFixed(2)}</strong>
                      </div>
                      <div className="relatorio-item">
                        <span>Total de Saídas:</span>
                        <strong>R$ {relatorioFechamento.movimentacoes.totalSaidas.toFixed(2)}</strong>
                      </div>

                      {Array.isArray(relatorioFechamento.movimentacoes.itens) && relatorioFechamento.movimentacoes.itens.length > 0 ? (
                        <div style={{ marginTop: '1rem' }}>
                          {relatorioFechamento.movimentacoes.itens.map((m) => (
                            <div
                              key={m.id}
                              style={{
                                padding: '0.75rem',
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: '6px',
                                marginBottom: '0.5rem',
                                borderLeft: `4px solid ${m.tipo === 'entrada' ? '#10B981' : '#EF4444'}`
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                <span style={{ fontWeight: 700 }}>
                                  {m.tipo === 'entrada' ? '⬆️ Entrada' : '⬇️ Saída'}
                                  {m.categoria ? ` • ${m.categoria}` : ''}
                                </span>
                                <strong>
                                  R$ {Number(m.valor || 0).toFixed(2)}
                                </strong>
                              </div>
                              <div style={{ fontSize: '0.85rem', opacity: 0.85, marginTop: '0.25rem' }}>
                                {m.descricao}
                              </div>
                              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                {m.banco ? `${m.banco}` : ''}{m.conta ? ` • ${m.conta}` : ''}
                                {m.data ? ` • ${new Date(m.data).toLocaleString('pt-BR')}` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ opacity: 0.8 }}>Nenhuma movimentação registrada no período.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <button 
                    className="btn btn-primary" 
                    onClick={() => {
                      setModalFechamento(false);
                      setRelatorioFechamento(null);
                      window.location.reload();
                    }}
                  >
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Prévia dos Cards */}
      {modalPrevia && (
        <div className="modal-overlay" onClick={() => setModalPrevia(null)}>
          <div className="modal-content modal-previa" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h3>Prévia do Caixa - {modalPrevia}</h3>
            {modalPrevia === 'Total em Caixa' && (
              <div className="previa-content">
                <div className="previa-item">
                  <span>Valor de Abertura:</span>
                  <strong>R$ {(caixaData?.valorAbertura || 0).toFixed(2)}</strong>
                </div>
                <div className="previa-item">
                  <span>Total de Vendas:</span>
                  <strong>R$ {totalVendas.toFixed(2)}</strong>
                </div>
                <div className="previa-item">
                  <span>Total em Caixa:</span>
                  <strong>R$ {valorEmCaixa.toFixed(2)}</strong>
                </div>
                <div className="previa-item">
                  <span>Quantidade de Vendas:</span>
                  <strong>{vendasFinalizadas.length}</strong>
                </div>
                <div className="previa-divider"></div>
                <h4>Vendas por Origem:</h4>
                <div className="previa-item">
                  <span>Balcão:</span>
                  <strong>{vendasBalcao}</strong>
                </div>
                <div className="previa-item">
                  <span>WhatsApp:</span>
                  <strong>{vendasChat}</strong>
                </div>
                <div className="previa-item">
                  <span>Retirada:</span>
                  <strong>{vendasRetirada}</strong>
                </div>
              </div>
            )}
            {modalPrevia === 'Total Vendido' && (
              <div className="previa-content">
                <div className="previa-item">
                  <span>Total:</span>
                  <strong>R$ {totalVendas.toFixed(2)}</strong>
                </div>
                <div className="previa-item">
                  <span>Quantidade:</span>
                  <strong>{vendasFinalizadas.length} vendas</strong>
                </div>
                <div className="previa-divider"></div>
                <h4>Últimas Vendas:</h4>
                <div className="vendas-lista-previa">
                  {vendasFinalizadas.slice(0, 5).map((venda, idx) => (
                    <div key={idx} className="venda-item-previa">
                      <span>#{venda.numero || idx + 1}</span>
                      <span>{venda.tipoVenda || 'balcao'}</span>
                      <strong>R$ {venda.total?.toFixed(2) || '0.00'}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {modalPrevia === 'Vendas Hoje' && (
              <div className="previa-content">
                <div className="previa-item">
                  <span>Total de Vendas:</span>
                  <strong>{vendasFinalizadas.length}</strong>
                </div>
                <div className="previa-divider"></div>
                <h4>Distribuição:</h4>
                <div className="previa-item">
                  <span>Balcão:</span>
                  <strong>{vendasBalcao} ({vendasFinalizadas.length > 0 ? ((vendasBalcao / vendasFinalizadas.length) * 100).toFixed(1) : 0}%)</strong>
                </div>
                <div className="previa-item">
                  <span>WhatsApp:</span>
                  <strong>{vendasChat} ({vendasFinalizadas.length > 0 ? ((vendasChat / vendasFinalizadas.length) * 100).toFixed(1) : 0}%)</strong>
                </div>
                <div className="previa-item">
                  <span>Retirada:</span>
                  <strong>{vendasRetirada} ({vendasFinalizadas.length > 0 ? ((vendasRetirada / vendasFinalizadas.length) * 100).toFixed(1) : 0}%)</strong>
                </div>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModalPrevia(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Delivery */}
      <ModalDelivery
        isOpen={modalDelivery}
        onClose={() => setModalDelivery(false)}
        estabelecimento={estabelecimento}
        caixaId={caixaData?._id}
      />
    </div>
  );
};

export default Caixa;

